'use client';
import React, { useRef, useState } from 'react';
import { QROptions } from './QRGeneratorClient';

interface QRCustomizationPanelProps {
  options: QROptions;
  onOptionsChange: (opts: QROptions) => void;
}

const errorLevels = [
  { key: 'L' as const, label: 'L', desc: '7%' },
  { key: 'M' as const, label: 'M', desc: '15%' },
  { key: 'Q' as const, label: 'Q', desc: '25%' },
  { key: 'H' as const, label: 'H', desc: '30%' },
];

const sizes = [
  { key: 256 as const, label: '256px', desc: 'Web' },
  { key: 512 as const, label: '512px', desc: 'Standard' },
  { key: 1024 as const, label: '1024px', desc: 'HD' },
  { key: 2048 as const, label: '2048px', desc: 'Print' },
];

interface AccordionSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({ title, icon, defaultOpen = false, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-5 transition-colors duration-150 hover:bg-secondary/50"
        style={{ minHeight: '56px' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-primary flex-shrink-0">{icon}</span>
          <span className="font-semibold text-foreground" style={{ fontSize: '14px' }}>{title}</span>
        </div>
        <svg
          width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease', color: 'var(--muted-foreground)', flexShrink: 0 }}
        >
          <path d="M4.5 7l4.5 4.5L13.5 7" />
        </svg>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

export default function QRCustomizationPanel({ options, onOptionsChange }: QRCustomizationPanelProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const update = (partial: Partial<QROptions>) => onOptionsChange({ ...options, ...partial });

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo file must be under 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      update({ logoUrl: ev.target?.result as string });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
      {/* Panel header */}
      <div className="px-5 py-4 border-b border-border">
        <h2 className="font-semibold text-foreground" style={{ fontSize: '15px' }}>Customization</h2>
        <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Tap a section to expand and customize</p>
      </div>

      {/* Colors Section */}
      <AccordionSection
        title="Colors"
        defaultOpen={true}
        icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="9" cy="9" r="7.5" />
            <path d="M9 1.5v15M1.5 9h15M3.2 3.2l11.6 11.6M14.8 3.2L3.2 14.8" />
          </svg>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Foreground</label>
            <div className="flex items-center gap-2.5 bg-secondary rounded-xl px-3 py-2.5 border border-border">
              <input
                type="color"
                value={options.fgColor}
                onChange={e => update({ fgColor: e.target.value })}
                className="color-swatch"
                title="Foreground color"
              />
              <input
                type="text"
                value={options.fgColor}
                onChange={e => update({ fgColor: e.target.value })}
                className="flex-1 bg-transparent border-none outline-none text-foreground font-mono min-w-0"
                style={{ fontSize: '12px' }}
                maxLength={7}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Background</label>
            <div className="flex items-center gap-2.5 bg-secondary rounded-xl px-3 py-2.5 border border-border">
              <input
                type="color"
                value={options.transparentBg ? '#FFFFFF' : options.bgColor}
                onChange={e => update({ bgColor: e.target.value })}
                className="color-swatch"
                disabled={options.transparentBg}
                title="Background color"
              />
              <input
                type="text"
                value={options.transparentBg ? 'transparent' : options.bgColor}
                onChange={e => update({ bgColor: e.target.value })}
                className="flex-1 bg-transparent border-none outline-none text-foreground font-mono min-w-0"
                style={{ fontSize: '12px' }}
                maxLength={7}
                disabled={options.transparentBg}
              />
            </div>
          </div>
        </div>

        {/* Transparent BG toggle */}
        <div className="flex items-center justify-between px-3 py-3 bg-secondary rounded-xl border border-border">
          <div>
            <span className="font-semibold text-foreground" style={{ fontSize: '13px' }}>Transparent Background</span>
            <p className="text-muted-foreground" style={{ fontSize: '11px' }}>PNG only</p>
          </div>
          <button
            onClick={() => update({ transparentBg: !options.transparentBg })}
            className="toggle-track flex-shrink-0"
            style={{ background: options.transparentBg ? 'var(--primary)' : 'var(--border)' }}
            aria-label="Toggle transparent background"
            role="switch"
            aria-checked={options.transparentBg}
          >
            <div
              className="toggle-thumb"
              style={{ transform: options.transparentBg ? 'translateX(20px)' : 'translateX(3px)' }}
            />
          </button>
        </div>
      </AccordionSection>

      {/* Logo Section */}
      <AccordionSection
        title="Logo"
        icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <rect x="1.5" y="3" width="15" height="12" rx="2" />
            <circle cx="6.5" cy="7.5" r="1.5" />
            <path d="M1.5 13l4-4 3 3 2.5-2.5 4 4" />
          </svg>
        }
      >
        {options.logoUrl ? (
          <div className="flex items-center gap-3 p-3 bg-secondary rounded-2xl border border-border">
            <img src={options.logoUrl} alt="Uploaded company logo" className="w-12 h-12 rounded-xl object-contain border border-border bg-white flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground" style={{ fontSize: '13px' }}>Logo uploaded</p>
              <p className="text-muted-foreground" style={{ fontSize: '12px' }}>Centered in QR code</p>
            </div>
            <button
              onClick={() => update({ logoUrl: null })}
              className="flex items-center justify-center rounded-xl border border-rose-200 text-rose-500 hover:bg-rose-50 flex-shrink-0"
              style={{ width: '36px', height: '36px' }}
              aria-label="Remove logo"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="2" y1="2" x2="12" y2="12" />
                <line x1="12" y1="2" x2="2" y2="12" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            onClick={() => logoInputRef.current?.click()}
            className="w-full border-2 border-dashed border-border rounded-2xl p-5 text-center hover:border-primary/40 hover:bg-primary/3 transition-all duration-200 group"
          >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(124,58,237,0.08) 100%)', border: '1px solid rgba(37,99,235,0.12)' }}>
              <svg width="20" height="20" viewBox="0 0 18 18" fill="none" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round">
                <path d="M9 11.5V4.5M9 4.5L6 7.5M9 4.5L12 7.5" />
                <path d="M3 13.5h12" />
              </svg>
            </div>
            <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Upload Logo</p>
            <p className="text-muted-foreground mt-1" style={{ fontSize: '12px' }}>PNG, SVG, JPG — max 2MB</p>
          </button>
        )}
        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />

        {options.logoUrl && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-foreground" style={{ fontSize: '13px' }}>Logo Size</label>
              <span className="badge badge-blue">{options.logoSize}%</span>
            </div>
            <input
              type="range"
              min={10}
              max={35}
              value={options.logoSize}
              onChange={e => update({ logoSize: Number(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-muted-foreground" style={{ fontSize: '11px' }}>
              <span>Smaller</span>
              <span>Larger</span>
            </div>
          </div>
        )}
      </AccordionSection>

      {/* Shape Section */}
      <AccordionSection
        title="Shape"
        icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <rect x="2" y="2" width="14" height="14" rx="4" />
          </svg>
        }
      >
        <div className="grid grid-cols-3 gap-3">
          {(['rounded', 'square', 'dots'] as const).map(style => (
            <button
              key={`style-${style}`}
              onClick={() => update({ style })}
              className={`flex flex-col items-center gap-2 p-3.5 rounded-2xl border transition-all duration-200 ${
                options.style === style
                  ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
              style={{ minHeight: '72px' }}
            >
              {style === 'rounded' && (
                <div className={`w-8 h-8 border-2 flex-shrink-0 rounded-lg ${options.style === style ? 'border-primary' : 'border-muted-foreground'}`}>
                  <div className={`m-1.5 w-2 h-2 rounded-sm ${options.style === style ? 'bg-primary' : 'bg-muted-foreground'}`} />
                </div>
              )}
              {style === 'square' && (
                <div className={`w-8 h-8 border-2 flex-shrink-0 rounded-sm ${options.style === style ? 'border-primary' : 'border-muted-foreground'}`}>
                  <div className={`m-1.5 w-2 h-2 rounded-none ${options.style === style ? 'bg-primary' : 'bg-muted-foreground'}`} />
                </div>
              )}
              {style === 'dots' && (
                <div className="w-8 h-8 flex-shrink-0 grid grid-cols-3 gap-0.5 p-0.5">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className={`rounded-full ${options.style === style ? 'bg-primary' : 'bg-muted-foreground'}`}
                    />
                  ))}
                </div>
              )}
              <span className="font-semibold capitalize" style={{ fontSize: '12px' }}>{style}</span>
            </button>
          ))}
        </div>
      </AccordionSection>

      {/* Error Correction Section */}
      <AccordionSection
        title="Error Correction"
        icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M9 1.5L16.5 5.5V9C16.5 13 13 16.2 9 17C5 16.2 1.5 13 1.5 9V5.5L9 1.5Z" />
            <path d="M6.5 9L8.2 10.8L11.5 7.5" />
          </svg>
        }
      >
        <p className="text-muted-foreground" style={{ fontSize: '12px' }}>Higher levels allow QR to scan even if partially obscured. Use H when adding a logo.</p>
        <div className="grid grid-cols-4 gap-2">
          {errorLevels.map(level => (
            <button
              key={`ec-${level.key}`}
              onClick={() => update({ errorLevel: level.key })}
              className={`flex flex-col items-center py-3 px-2 rounded-xl border transition-all duration-200 ${
                options.errorLevel === level.key
                  ? 'border-transparent text-white shadow-blue'
                  : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
              style={{
                background: options.errorLevel === level.key ? 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' : undefined,
                minHeight: '56px',
              }}
            >
              <span className="font-bold" style={{ fontSize: '18px' }}>{level.label}</span>
              <span style={{ fontSize: '10px', fontWeight: 500, opacity: 0.8 }}>{level.desc}</span>
            </button>
          ))}
        </div>
      </AccordionSection>

      {/* Size Section */}
      <AccordionSection
        title="Output Size"
        icon={
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <rect x="1.5" y="1.5" width="15" height="15" rx="2" />
            <path d="M5 9h8M9 5v8" />
          </svg>
        }
      >
        <div className="grid grid-cols-2 gap-2.5">
          {sizes.map(size => (
            <button
              key={`size-${size.key}`}
              onClick={() => update({ size: size.key })}
              className={`flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-200 ${
                options.size === size.key
                  ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
              style={{ minHeight: '52px' }}
            >
              <span className="font-semibold" style={{ fontSize: '13px' }}>{size.label}</span>
              <span style={{ fontSize: '11px', opacity: 0.7 }}>{size.desc}</span>
            </button>
          ))}
        </div>
      </AccordionSection>
    </div>
  );
}